import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { RefreshCw, BarChart3, LineChart, PieChart as PieChartIcon } from 'lucide-react';
import {
  BarChart as RechartsBarChart,
  LineChart as RechartsLineChart,
  PieChart as RechartsPieChart,
  Bar,
  Line,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const ChartDisplay = ({ component, customization = {} }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  // Parse config
  const config = typeof component.ConfigJSON === 'string' 
    ? JSON.parse(component.ConfigJSON) 
    : component.ConfigJSON || component.config || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const componentType = component.ComponentType || component.componentType;
  const chartType = componentType === 'BarChart' ? 'bar' 
    : componentType === 'LineChart' ? 'line' 
    : componentType === 'PieChart' ? 'pie' 
    : config.chartType || 'bar';

  useEffect(() => {
    loadData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [component.ComponentID]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${component.ComponentID}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load chart data');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Error loading chart data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Color palettes for pie charts (matching ChartEditor and PreviewPanel)
  const COLOR_PALETTES = {
    vibrant: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'],
    pastel: ['#A5D8FF', '#A7F3D0', '#FDE68A', '#FCA5A5', '#C4B5FD', '#67E8F9', '#F9A8D4', '#D9F99D', '#FDBA74', '#C7D2FE'],
    monochrome: ['#1F2937', '#374151', '#4B5563', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB', '#F3F4F6', '#F9FAFB', '#FFFFFF'],
    warm: ['#F97316', '#EF4444', '#DC2626', '#F59E0B', '#FBBF24', '#FCD34D', '#FED7AA', '#FFEDD5', '#FFF7ED', '#FEF2F2'],
    cool: ['#3B82F6', '#2563EB', '#1D4ED8', '#10B981', '#059669', '#06B6D4', '#0891B2', '#0EA5E9', '#60A5FA', '#93C5FD'],
    sunset: ['#F97316', '#EC4899', '#8B5CF6', '#6366F1', '#3B82F6', '#10B981', '#84CC16', '#FBBF24', '#F59E0B', '#EF4444']
  };
  
  // Default colors for bar/line charts (fallback)
  const DEFAULT_COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'
  ];

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-red-200 dark:border-red-800">
        <div className="text-red-600 dark:text-red-400 text-sm">
          Error loading chart: {error}
        </div>
        <button
          onClick={loadData}
          className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center h-[300px]">
          <div className="flex flex-col items-center">
            <RefreshCw className="w-8 h-8 animate-spin text-teal-600 mb-2" />
            <span className="text-gray-400">Loading chart...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          {customization.title || component.Name}
        </h3>
        <div className="flex items-center justify-center h-[300px]">
          <div className="text-center">
            {chartType === 'pie' ? (
              <PieChartIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            ) : chartType === 'line' ? (
              <LineChart className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            ) : (
              <BarChart3 className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">No data available</p>
            {(!config.xAxis && chartType !== 'pie') && (
              <p className="text-xs mt-1 text-gray-400">Configure X-axis field</p>
            )}
            {chartType === 'pie' && !config.xAxis && (
              <p className="text-xs mt-1 text-gray-400">Configure category field</p>
            )}
            {(!config.series || config.series.length === 0) && (
              <p className="text-xs mt-1 text-gray-400">Add at least one data series</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const chartData = data.data || [];
  const series = config.series || [];

  if (chartType === 'pie') {
    // Pie chart: data should have 'category' and 'value' fields
    const pieData = chartData.map(item => ({
      name: item.category || item.name || 'Unknown',
      value: parseFloat(item.value || 0)
    }));

    const showDataLabels = config.showDataLabels !== false; // Default to true
    const showAxisLabels = config.showAxisLabels !== false; // Default to true (for legend)
    
    // Use selected palette or default to vibrant
    const selectedPalette = COLOR_PALETTES[config.colorPalette || 'vibrant'] || COLOR_PALETTES.vibrant;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {customization.title || component.Name}
          </h3>
          <button
            onClick={loadData}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {component.Description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-shrink-0">
            {component.Description}
          </p>
        )}
        <div className="flex-1 min-h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart margin={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={showDataLabels}
              label={showDataLabels ? ({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)` : false}
              outerRadius="65%"
              fill="#8884d8"
              dataKey="value"
            >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={selectedPalette[index % selectedPalette.length]} />
                ))}
              </Pie>
              <Tooltip />
              {showAxisLabels && <Legend />}
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Bar/Line chart: data should have 'category' and series fields
  const categoryKey = 'category';
  const firstRow = chartData[0] || {};
  const availableKeys = Object.keys(firstRow);
  
  // Check if we have series configured
  if (!series || series.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          {customization.title || component.Name}
        </h3>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No series configured. Please add at least one data series.</p>
        </div>
      </div>
    );
  }
  
  // Map series to keys (same logic as PreviewPanel)
  const seriesKeys = series.map((s, index) => {
    let expectedKey = s.label || s.field || `series${index + 1}`;
    expectedKey = expectedKey.replace(/[^a-zA-Z0-9_]/g, '_');
    if (/^[0-9]/.test(expectedKey)) {
      expectedKey = `series_${expectedKey}`;
    }
    
    let key = null;
    if (expectedKey in firstRow) {
      key = expectedKey;
    } else {
      const foundKey = availableKeys.find(k => k.toLowerCase() === expectedKey.toLowerCase());
      if (foundKey) {
        key = foundKey;
      } else {
        const partialMatch = availableKeys.find(k => 
          k.toLowerCase().includes((s.field || '').toLowerCase()) ||
          (s.label && k.toLowerCase().includes(s.label.toLowerCase()))
        );
        key = partialMatch || expectedKey;
      }
    }
    
    return { 
      key, 
      label: s.label || s.field || `Series ${index + 1}`, 
      color: s.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length] 
    };
  }).filter(s => s.key && chartData[0] && s.key in chartData[0]);

  if (seriesKeys.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          {customization.title || component.Name}
        </h3>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No series configured. Please add at least one data series.</p>
        </div>
      </div>
    );
  }

  const ChartComponent = chartType === 'bar' ? RechartsBarChart : RechartsLineChart;
  const DataComponent = chartType === 'bar' ? Bar : Line;
  const showDataLabels = config.showDataLabels !== false; // Default to true
  const showAxisLabels = config.showAxisLabels !== false; // Default to true
  
  // Get bar orientation for bar charts
  const barLayout = chartType === 'bar' ? (config.barOrientation || 'vertical') : null;
  const isHorizontalBar = barLayout === 'horizontal';

  // Data label formatter - show value on each bar/line point
  const renderLabel = showDataLabels ? (entry) => {
    // For multiple series, show the first series value or sum
    if (seriesKeys.length > 0) {
      const value = entry[seriesKeys[0].key] || 0;
      return value;
    }
    return entry.value || 0;
  } : false;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {customization.title || component.Name}
          </h3>
          {component.Description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {component.Description}
            </p>
          )}
        </div>
        <button
          onClick={loadData}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <ChartComponent 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            layout={isHorizontalBar ? 'horizontal' : undefined}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {showAxisLabels ? (
            <>
              {isHorizontalBar ? (
                <>
                  <XAxis type="number" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                  <YAxis 
                    dataKey={categoryKey} 
                    type="category" 
                    width={80}
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                </>
              ) : (
                <>
                  <XAxis 
                    dataKey={categoryKey} 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {isHorizontalBar ? (
                <>
                  <XAxis type="number" hide={true} />
                  <YAxis dataKey={categoryKey} type="category" hide={true} />
                </>
              ) : (
                <>
                  <XAxis 
                    dataKey={categoryKey} 
                    hide={true}
                  />
                  <YAxis 
                    hide={true}
                  />
                </>
              )}
            </>
          )}
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#fff', 
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <Legend />
          {seriesKeys.map((series, index) => (
            <DataComponent
              key={series.key}
              type="monotone"
              dataKey={series.key}
              name={series.label}
              stroke={series.color}
              fill={series.color}
              label={showDataLabels ? renderLabel : false}
            />
          ))}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartDisplay;

